import { z } from 'zod';
/**
 * Payers, service codes and rates (slice-01 §6, spec §6.3.3).
 *
 * ## Money crosses the wire as a string
 *
 * `amountCents` is `bigint` in the database and in the domain, per CLAUDE.md —
 * no floats in billing, ever. JSON has no bigint, and `JSON.stringify` throws
 * on one rather than quietly rounding, which is the one merciful thing about
 * it.
 *
 * So it travels as a decimal string of whole cents: `"725"` is $7.25. Not a
 * JSON number — a number would survive today, because 2^53 cents is ninety
 * trillion dollars, and would then be the reason somebody later reaches for a
 * float when a total needs a fraction of a cent. The string closes that door at
 * the boundary rather than trusting every future caller to stay under the
 * limit.
 */
const centsSchema = z
    .string()
    .regex(/^\d+$/, 'Give an amount in whole cents, digits only — 725 is $7.25.');
export const payerTypeSchema = z.enum([
    'MEDICAID',
    'MEDICAID_MCO',
    'PRIVATE_PAY',
    'LTC_INSURANCE',
    'VA',
    'WORKERS_COMP',
    'OTHER',
]);
export const billingMethodSchema = z.enum(['EDI_837P', 'STATE_PORTAL', 'INVOICE', 'MANUAL']);
export const roundingPolicySchema = z.enum([
    'EXACT_UNITS',
    'NEAREST_UNIT',
    'ROUND_DOWN',
    'ROUND_UP',
    'SEVEN_MINUTE_RULE',
    'CUSTOM',
]);
export const claimFrequencySchema = z.enum(['WEEKLY', 'BIWEEKLY', 'SEMIMONTHLY', 'MONTHLY']);
/**
 * Which week or month a service date falls in.
 *
 * **Required, with no default, anywhere it can be set.** §6.3.3 calls this out
 * and R-1b names the consequence: a wrong anchor silently mis-caps every weekly
 * authorization, and nothing fails at the moment the mistake is made. Visits
 * schedule, care is delivered, and it surfaces months later as a denied claim.
 *
 * A default would be a guess about a payer's contract wearing the clothes of a
 * sensible fallback. There is no anchor that is right more often than the
 * others — payers genuinely differ — so the only safe behaviour is to refuse to
 * create the payer until somebody has looked it up.
 */
export const periodAnchorSchema = z.enum([
    'SUN_SAT',
    'MON_SUN',
    'CALENDAR_MONTH',
    'AUTH_ANNIVERSARY',
]);
export const unitTypeSchema = z.enum(['MINUTES_15', 'MINUTES_30', 'HOUR', 'DAY', 'VISIT']);
export const disciplineSchema = z.enum([
    'PCA',
    'HHA',
    'HOMEMAKER',
    'COMPANION',
    'RESPITE',
    'NURSING_SUPERVISORY',
    'OTHER',
]);
export const rateTypeSchema = z.enum(['BILL', 'PAY']);
export const rateDifferentialSchema = z.enum([
    'STANDARD',
    'WEEKEND',
    'HOLIDAY',
    'OVERNIGHT',
    'LIVE_IN',
    'HOLIDAY_OVERTIME',
]);
// ---------------------------------------------------------------------------
// Responses
// ---------------------------------------------------------------------------
export const payerSchema = z.object({
    id: z.string().uuid(),
    name: z.string(),
    type: payerTypeSchema,
    stateCode: z.string().nullable(),
    payerIdCode: z.string().nullable(),
    billingMethod: billingMethodSchema,
    requiresEvv: z.boolean(),
    unitMinutes: z.number().int(),
    roundingPolicy: roundingPolicySchema,
    timelyFilingDays: z.number().int().nullable(),
    claimFrequency: claimFrequencySchema.nullable(),
    periodAnchor: periodAnchorSchema,
    requiresEvvAcceptanceBeforeClaim: z.boolean(),
});
export const serviceCodeSchema = z.object({
    id: z.string().uuid(),
    payerId: z.string().uuid(),
    code: z.string(),
    modifiers: z.array(z.string()),
    description: z.string().nullable(),
    unitType: unitTypeSchema,
    requiresEvv: z.boolean(),
    evvServiceCode: z.string().nullable(),
    placeOfService: z.string().nullable(),
    discipline: disciplineSchema,
    isActive: z.boolean(),
});
export const rateSchema = z.object({
    id: z.string().uuid(),
    payerId: z.string().uuid(),
    serviceCodeId: z.string().uuid(),
    clientId: z.string().uuid().nullable(),
    rateType: rateTypeSchema,
    amountCents: centsSchema,
    differential: rateDifferentialSchema,
    minShiftMinutes: z.number().int().nullable(),
    effectiveFrom: z.string(),
    effectiveTo: z.string().nullable(),
});
export const payerListSchema = z.object({ payers: z.array(payerSchema) });
export const serviceCodeListSchema = z.object({ serviceCodes: z.array(serviceCodeSchema) });
export const rateListSchema = z.object({ rates: z.array(rateSchema) });
// ---------------------------------------------------------------------------
// Requests
// ---------------------------------------------------------------------------
const isoDate = z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Use a date like 2026-03-11 — four digits, two, two.');
export const createPayerSchema = z.object({
    name: z.string().trim().min(1).max(200),
    type: payerTypeSchema,
    stateCode: z.string().trim().length(2).nullish(),
    payerIdCode: z.string().trim().max(64).nullish(),
    billingMethod: billingMethodSchema,
    requiresEvv: z.boolean().default(false),
    unitMinutes: z.number().int().positive().max(1_440).default(15),
    roundingPolicy: roundingPolicySchema.default('EXACT_UNITS'),
    timelyFilingDays: z.number().int().positive().max(3_650).nullish(),
    claimFrequency: claimFrequencySchema.nullish(),
    /**
     * No `.default()`, deliberately. See {@link periodAnchorSchema}: the only
     * safe behaviour is to refuse the payer until somebody has read the contract.
     */
    periodAnchor: periodAnchorSchema,
    requiresEvvAcceptanceBeforeClaim: z.boolean().default(false),
});
/**
 * `periodAnchor` is changeable but never omissible.
 *
 * A payer that was set up wrong has to be correctable — that is the whole point
 * of finding out months later. What must not happen is a PATCH that leaves it
 * out and a caller who believes they set it: `.partial()` makes every field
 * optional, and an omitted anchor means unchanged, which is the correct and
 * safe reading. The service emits a distinct audit action when it does move,
 * because re-anchoring a payer re-caps every authorization under it.
 */
export const updatePayerSchema = createPayerSchema.partial().strict();
export const createServiceCodeSchema = z.object({
    payerId: z.string().uuid(),
    code: z.string().trim().min(1).max(32),
    /** Up to four, ordered — order is meaningful on an 837P. */
    modifiers: z.array(z.string().trim().min(1).max(8)).max(4).default([]),
    description: z.string().trim().max(500).nullish(),
    unitType: unitTypeSchema,
    requiresEvv: z.boolean().default(false),
    evvServiceCode: z.string().trim().max(32).nullish(),
    placeOfService: z.string().trim().length(2).nullish(),
    discipline: disciplineSchema,
    isActive: z.boolean().default(true),
});
/** `payerId` is absent: a service code does not move between payers. */
export const updateServiceCodeSchema = createServiceCodeSchema
    .omit({ payerId: true })
    .partial()
    .strict();
/**
 * Creating a rate. There is no update counterpart, and that is the rule.
 *
 * §6.3.3: **never update a rate row; insert a new one and end-date the old.** A
 * rate that changes in place rewrites history — every charge already computed
 * from it silently disagrees with the row that produced it, and reconstructing
 * what an agency actually billed in March becomes impossible.
 *
 * The API enforces that by not having the route. There is no `PATCH /v1/rates/
 * :id` and no `PUT`, so the mistake is not available rather than discouraged,
 * and an attempt answers 404 because the endpoint genuinely does not exist.
 *
 * End-dating is `effectiveTo` on the new row's predecessor, which the create
 * endpoint does in the same transaction — see `supersedesRateId`.
 */
export const createRateSchema = z.object({
    payerId: z.string().uuid(),
    serviceCodeId: z.string().uuid(),
    /** Client-specific override. Null means the rate applies to every client. */
    clientId: z.string().uuid().nullish(),
    rateType: rateTypeSchema,
    amountCents: centsSchema,
    differential: rateDifferentialSchema.default('STANDARD'),
    minShiftMinutes: z.number().int().positive().max(1_440).nullish(),
    effectiveFrom: isoDate,
    effectiveTo: isoDate.nullish(),
    /**
     * The row this one replaces, end-dated to the day before `effectiveFrom`.
     *
     * Optional, and doing it here rather than in a second request is the point:
     * end-dating and inserting are one change to what an agency bills, and split
     * across two calls there is a window where both rates are live and the
     * resolver picks by `effective_from` — which is a real answer to a question
     * nobody meant to ask.
     */
    supersedesRateId: z.string().uuid().nullish(),
});
export const payerListQuerySchema = z.object({
    type: payerTypeSchema.optional(),
});
export const serviceCodeListQuerySchema = z.object({
    payerId: z.string().uuid().optional(),
    /**
     * Left as the literal strings a query string actually carries.
     *
     * A `.transform()` to boolean would make the schema's input and output types
     * differ, and `ZodValidationPipe` requires them equal — deliberately, because
     * a pipe whose output type is not its input type is a pipe that can silently
     * hand a controller something other than what it declared. The service reads
     * `=== 'true'`, which is one comparison in one place.
     */
    isActive: z.enum(['true', 'false']).optional(),
});
export const rateListQuerySchema = z.object({
    payerId: z.string().uuid().optional(),
    serviceCodeId: z.string().uuid().optional(),
    clientId: z.string().uuid().optional(),
    rateType: rateTypeSchema.optional(),
    /** Only rates effective on this date. Omitted means every row, ever. */
    on: isoDate.optional(),
});
//# sourceMappingURL=payer.js.map